'use client';

import { useState, useMemo } from "react";
import { COSTE_DE_SERVICIO } from "@/config/facturacion";
import { round } from "@/helpers/currency";
import { formatSeatLabel } from "@/lib/utils/seatLabel";
import Image from "next/image";

interface EntradaSeleccionada {
  tipoEntradaId: string;
  cantidad: number;
  nombre?: string;
  precio?: number;
  asientos?: Array<{ seatId: string; mesaId?: string }>;
}

type Props = {
  nombre: string;
  flyer: string;
  entradas: EntradaSeleccionada[];
  onClick: () => void;
}

export function ResumenCompra({ nombre, flyer, entradas, onClick }: Props) {
  // Estado local para controlar si se muestra o no el modal de advertencia
  const [mostrarAlerta, setMostrarAlerta] = useState(false);

  const totalEntradas = entradas.reduce((acc, e) => acc + (e.precio ?? 0) * e.cantidad, 0);
  const costeDeServicio = totalEntradas * COSTE_DE_SERVICIO;
  const total = totalEntradas + costeDeServicio;

  // Lógica para detectar si hay asientos en mesas distintas
  const tieneMesasDiferentes = useMemo(() => {
    const mesasUnicas = new Set<string>();

    entradas.forEach(e => {
      e.asientos?.forEach(a => {
        if (a.mesaId) {
          mesasUnicas.add(String(a.mesaId));
        }
      });
    });

    // Si el set tiene más de 1 ID de mesa registrado, hay un conflicto
    return mesasUnicas.size > 1;
  }, [entradas]);

  // Interceptamos la acción del botón principal
  const handleSiguienteClick = () => {
    if (tieneMesasDiferentes) {
      setMostrarAlerta(true); // Frena y abre el cartel
    } else {
      onClick(); // Flujo normal sin interrupciones
    }
  };

  // Acción cuando el cliente presiona que está de acuerdo en el modal
  const handleConfirmarYContinuar = () => {
    setMostrarAlerta(false);
    onClick(); // Ejecuta el flujo original de compra
  };

  return (
    <>
      <section className="text-white space-y-6">
        {/* Encabezado */}
        <h3 className="text-xl font-semibold border-b border-white/30 pb-2">
          Resumen
        </h3>

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 relative rounded-md">
            <img
              src={flyer || "/bebop/placeholder/flyer_ticketera.png"}
              alt={nombre}
              className="w-full h-full object-cover rounded-md"
              onError={(e) => { (e.target as HTMLImageElement).src = "/bebop/placeholder/flyer_ticketera.png"; }}
            />
          </div>
          <div>
            <h2 className="text-lg font-display uppercase">{nombre}</h2>
            <p className="text-sm text-white/70">Resumen de compra</p>
          </div>
        </div>

        {/* Detalle de entradas */}
        <div className="space-y-2 text-sm">
          {entradas.map((e, idx) => {
            const asientosValidos = e.asientos?.filter(a => a.seatId != null) || [];

            return (
              <div key={e.tipoEntradaId}>
                <div className="flex justify-between">
                  <span>
                    {e.cantidad} x {e.nombre ?? `Entrada #${e.tipoEntradaId}`}
                  </span>
                  <span>${(e.precio ?? 0) * e.cantidad}</span>
                </div>
                {asientosValidos.length > 0 && (
                  <div className="text-xs text-white/60 mt-1 pl-4">
                    Asientos: {asientosValidos.map(a => formatSeatLabel(a.seatId)).join(', ')}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-between">
            <span>Coste de servicio</span>
            <span>${costeDeServicio.toFixed(2)}</span>
          </div>
        </div>

        {/* Totales */}
        <div className="border-t border-white/20 pt-4 space-y-2 text-sm">
          <div className="flex justify-between font-bold text-base">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </section>

      {/* Botón Siguiente modificado con nuestro interceptor */}
      <button onClick={handleSiguienteClick} className='btn w-full mt-4'>
        Siguiente
      </button>

      {/* COMPONENTE MODAL INTERMITENTE DE ADVERTENCIA */}
      {mostrarAlerta && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
          <div className="bg-[#1c1c1c] border border-red-500/40 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">

            {/* Encabezado del aviso */}
            <div className="bg-red-950/50 p-5 flex items-start gap-4 border-b border-red-500/20">
              <span className="text-red-500 text-3xl leading-none">⚠️</span>
              <div>
                <h4 className="text-white font-bold uppercase text-base tracking-wide mb-1">
                  Advertencia de Ubicación
                </h4>
                <p className="text-red-200/90 text-xs font-medium">
                  Detectamos asientos seleccionados en mesas distintas.
                </p>
              </div>
            </div>

            {/* Cuerpo del mensaje */}
            <div className="p-6 space-y-5">
              <p className="text-gray-300 text-sm leading-relaxed text-center">
                Tené en cuenta que la distribución en la sala es fija. Al estar en <strong className="text-red-400 uppercase">mesas separadas</strong>, los asistentes no podrán sentarse juntos durante el show.
              </p>

              <p className="text-gray-400 text-xs text-center font-semibold">
                ¿Estás seguro de que querés continuar con la compra bajo estas condiciones?
              </p>

              {/* Botonera de decisión */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setMostrarAlerta(false)}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-lg transition-colors text-xs uppercase tracking-wider border border-white/5"
                >
                  Modificar
                </button>
                <button
                  onClick={handleConfirmarYContinuar}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors text-xs uppercase tracking-wider shadow-lg shadow-red-900/40"
                >
                  Sí, Continuar
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
}